/**
 * Switch: a sensor pad on the ground/wall that toggles or holds open all
 * gates sharing its `id`.
 *
 *   - mode='toggle' (default): the first overlap latches the switch on and
 *     it stays on for the rest of the run. One-shot activation avoids fast
 *     on/off oscillation when several ragdoll bodies graze the pad on a
 *     single pass.
 *   - mode='momentary': gates with the matching id are forced open while
 *     the player is overlapping the pad, then snap closed when the player
 *     leaves. Useful for "hold position to keep door open" puzzles.
 *
 * The body is a Matter sensor (no collision response) labelled 'switch';
 * GameScene drives state transitions from collisionStart / overlap each
 * tick rather than from generic collision dispatch so the toggle edge is
 * unambiguous.
 *
 * Visual: a flat pad with a centred glyph (◀▶ for toggle, ◯ for momentary)
 * tinted by `id` so multiple switches in a level read as different
 * circuits at a glance.
 */

import Matter from 'matter-js';
import { Container, Graphics } from 'pixi.js';

export type SwitchMode = 'toggle' | 'momentary';

export interface SwitchOptions {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Circuit id; gates with the same id react to this switch. */
  id: string;
  mode?: SwitchMode;
}

export class Switch {
  readonly body: Matter.Body;
  readonly view: Container;
  readonly id: string;
  readonly mode: SwitchMode;
  /** Stable accent colour derived from id, shared with matching gates. */
  readonly tint: number;

  /** True while any ragdoll body is currently inside this pad's bounds. */
  overlapping = false;
  /** Toggle-mode latched state: flips on each fresh overlap. */
  pressed = false;

  private readonly base: Graphics;
  private readonly cap: Graphics;
  private readonly glow: Graphics;
  private readonly capBaseY: number;
  private readonly capW: number;
  private readonly capH: number;
  private readonly capCx: number;
  private readonly capCy: number;
  private readonly isToggle: boolean;
  /** Eased depressed weight (0..1). */
  private animPress = 0;
  /** Spring state for bounce: position offset (px) and velocity (px/frame). */
  private springY = 0;
  private springVy = 0;
  /** Last "logical on" we rendered, so we can spring on transitions. */
  private lastOn = false;

  constructor(opts: SwitchOptions) {
    this.id = opts.id;
    this.mode = opts.mode ?? 'toggle';
    this.tint = colorForId(opts.id);
    this.isToggle = this.mode === 'toggle';

    this.body = Matter.Bodies.rectangle(
      opts.x + opts.w / 2,
      opts.y + opts.h / 2,
      opts.w,
      opts.h,
      { isStatic: true, isSensor: true, label: 'switch' },
    );

    const c = new Container();

    // Soft outer halo (sits behind everything; brightens when active).
    const glow = new Graphics();
    const padX = 6, padY = 6;
    glow.roundRect(opts.x - padX, opts.y - padY, opts.w + padX * 2, opts.h + padY * 2, 8)
      .fill({ color: this.tint, alpha: 0.10 });
    c.addChild(glow);

    // Recessed metal housing with rivets at the corners — reads as a
    // physical pad you can press, not just a decal.
    const base = new Graphics();
    base.roundRect(opts.x, opts.y, opts.w, opts.h, 5)
      .fill({ color: 0x1a1018, alpha: 0.92 })
      .stroke({ color: this.tint, width: 2, alpha: 0.85 });
    // Inner bevel (lighter top/left edge).
    base.roundRect(opts.x + 1.5, opts.y + 1.5, opts.w - 3, opts.h - 3, 4)
      .stroke({ color: 0xffffff, width: 0.8, alpha: 0.18 });
    // Rivets.
    const rivetR = Math.min(2.2, Math.min(opts.w, opts.h) * 0.06);
    for (const [rx, ry] of [
      [opts.x + 4, opts.y + 4],
      [opts.x + opts.w - 4, opts.y + 4],
      [opts.x + 4, opts.y + opts.h - 4],
      [opts.x + opts.w - 4, opts.y + opts.h - 4],
    ] as const) {
      base.circle(rx, ry, rivetR).fill({ color: 0x000000, alpha: 0.6 });
      base.circle(rx - 0.4, ry - 0.4, rivetR * 0.45).fill({ color: 0xffffff, alpha: 0.35 });
    }
    c.addChild(base);

    // Pressable cap. Drawn so its centre is at (0,0) inside its own
    // graphics so we can translate it without re-tessellating.
    const inset = 2;
    const capW = opts.w - inset * 2;
    const capH = opts.h - inset * 2;
    const capCx = opts.x + opts.w / 2;
    const capCy = opts.y + opts.h / 2;
    const cap = new Graphics();
    cap.roundRect(-capW / 2, -capH / 2, capW, capH, 3)
      .fill({ color: this.tint, alpha: 0.65 })
      .stroke({ color: 0xffffff, width: 1.4, alpha: 0.7 });
    // Top highlight stripe (gives it a domed look).
    cap.roundRect(-capW / 2 + 1.5, -capH / 2 + 1.5, capW - 3, Math.max(2, capH * 0.32), 2)
      .fill({ color: 0xffffff, alpha: 0.18 });
    // Glyph: arrows for toggle, ring for momentary — drawn centred.
    const r = Math.min(capW, capH) * 0.22;
    if (this.isToggle) {
      cap.moveTo(-r, 0).lineTo(-r * 0.35, -r).lineTo(-r * 0.35, r).closePath().fill(0xffffff);
      cap.moveTo(r, 0).lineTo(r * 0.35, -r).lineTo(r * 0.35, r).closePath().fill(0xffffff);
    } else {
      cap.circle(0, 0, r).stroke({ color: 0xffffff, width: 1.6 });
      cap.circle(0, 0, r * 0.45).fill({ color: 0xffffff, alpha: 0.85 });
    }
    cap.x = capCx;
    cap.y = capCy;
    c.addChild(cap);

    this.glow = glow;
    this.base = base;
    this.cap = cap;
    this.capBaseY = capCy;
    this.capW = capW;
    this.capH = capH;
    this.capCx = capCx;
    this.capCy = capCy;
    this.view = c;
  }

  /** Per-frame visuals. Bouncy spring on state change + smooth depress. */
  render(): void {
    const on = this.overlapping || (this.isToggle && this.pressed);
    const target = on ? 1 : 0;
    this.animPress += (target - this.animPress) * 0.28;

    // Kick the spring on every rising/falling logical edge.
    if (on !== this.lastOn) {
      // Rising edge: punch down a bit; falling edge: pop up.
      this.springVy += on ? 1.6 : -1.2;
      this.lastOn = on;
    }
    // Critically-ish damped spring back to neutral.
    const k = 0.35;       // stiffness
    const damp = 0.62;    // 0..1 (lower = bouncier)
    this.springVy += -this.springY * k;
    this.springVy *= damp;
    this.springY += this.springVy;

    // Cap rest dip when held down (~2px), plus the spring offset.
    const dip = this.animPress * 2;
    this.cap.y = this.capBaseY + dip + this.springY;
    // Subtle horizontal squash/scale to sell the bounce.
    const sq = 1 - Math.min(0.18, Math.abs(this.springY) * 0.04);
    this.cap.scale.set(1 / sq, sq);
    this.cap.alpha = 0.65 + this.animPress * 0.35;
    // Halo + housing glow with state.
    this.glow.alpha = 0.18 + this.animPress * 0.55;
    this.base.alpha = 0.92 + this.animPress * 0.08;
    void this.capW; void this.capH; void this.capCx; void this.capCy;
  }
}

/** Hash an id string into a hue and return an HSL→hex tint. Stable across
 *  reloads so the same circuit always reads the same colour. */
function colorForId(id: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  const hue = (h % 360) / 360;
  return hsl(hue, 0.65, 0.62);
}

function hsl(h: number, s: number, l: number): number {
  const f = (n: number): number => {
    const k = (n + h * 12) % 12;
    const a = s * Math.min(l, 1 - l);
    const v = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(v * 255);
  };
  return (f(0) << 16) | (f(8) << 8) | f(4);
}

/** Exposed for the editor + Gate entity so a circuit's two halves share a
 *  colour. Pure function of the id string. */
export function switchTintForId(id: string): number {
  return colorForId(id);
}
